import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHeaderButtons } from '../../../component/header/HeaderButtonProvider';
import SquareButton from '../../../component/button/SquareButton';
import { AttendanceUploadModal } from '../../../component/common/modal/ModalComponents/AttendanceUploadModal';
import styled, { css } from 'styled-components';
import { ja } from '@faker-js/faker/.';
import { attendanceSample } from '../../../@const/sampleData';
import { useAttendanceExcelDownload } from '../../../function/createExcel';
import toast from 'react-hot-toast';
import {
  LayoutDataType,
  AttendanceResultWrapper,
  AttendanceWrapper,
  AttendanceResultType,
  AttendanceType,
} from '../services/Attendance/type';
import {
  totalDateFn,
  shortenedWorkHourFn,
  overworkHourFn,
  annualLeaveHourFn,
  absentDaysFn,
  sickLeaveHourFn,
} from '../services/Attendance/service';

export const Attendance = () => {
  const { generate } = useAttendanceExcelDownload();
  const [isPartTime, setIsPartTime] = useState<boolean>(false);
  const { setButtons } = useHeaderButtons();
  const [openUploadModal, setOpenUploadModal] = useState<boolean>(false);
  const [layoutDatas, setLayoutDatas] = useState<LayoutDataType[]>([]);
  const [selectedLayoutData, setSelectedLayoutData] =
    useState<LayoutDataType | null>(null);
  const [selectedName, setSelectedName] = useState<string | null>(null);
  const [attendanceResults, setAttendanceResults] = useState<
    AttendanceResultWrapper[]
  >([]);
  const [selectedAttendanceResult, setSelectedAttendanceResult] =
    useState<AttendanceResultWrapper | null>(null);
  const [selectedWrapper, setSelectedWrapper] =
    useState<AttendanceWrapper | null>(null);
  const [fixedNames, setFixedNames] = useState<string[]>([]);
  const [year, setYear] = useState<string | null>(null);
  const [month, setMonth] = useState<string | null>(null);
  const [attendanceWrappers, setAttendanceWrappers] = useState<
    AttendanceWrapper[]
  >([]);
  useEffect(() => {
    //헤더에 버튼 추가
    setButtons(
      <>
        <SquareButton
          text='파일 등록'
          colorType={year && month ? 'ACTIVE' : 'DISACTIVE'}
          onClick={() => (year && month ? setOpenUploadModal(true) : undefined)}
        />
        <SquareButton
          text='파일 저장'
          colorType={'ACTIVE'}
          onClick={() => generate(attendanceWrappers, attendanceResults)}
        />
        <SquareButton
          text='개별 삭제'
          colorType={isRemovable() ? 'ACTIVE_WARN' : 'DISACTIVE'}
          onClick={() => isRemovable() && removeData()}
        />
      </>
    );
    // 페이지가 바뀌면 버튼 초기화
    return () => setButtons(null);
  }, [
    setButtons,
    year,
    month,
    attendanceWrappers,
    attendanceResults,
    selectedName,
  ]);

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value; // "YYYY-MM" 형식

    // 연도와 월 분리
    const [year, month] = value.split('-');
    console.log(year, month);
    setYear(year);
    setMonth(month);
  };

  function onSelectExcelData(data: AttendanceWrapper[]) {
    const isInvalid = data
      .flatMap((att) => att.data)
      .some(({ date }) => {
        const [rowYear, rowMonth] = date.split('-');
        return (
          Number(rowYear) !== Number(year) || Number(rowMonth) !== Number(month)
        );
      });

    if (isInvalid) {
      toast.error(
        '선택한 연/월과 일치하지 않는 데이터가 존재합니다. 다시 시도해주세요.'
      );
      return;
    }

    //데이터 추가 시, 기존 데이터와 중복검사
    if (
      attendanceResults.some((exist) =>
        data.some((newData) => exist.name === newData.name)
      )
    ) {
      toast.error(
        '추가된 데이터에 기존 데이터와 중복되는 항목이 존재합니다.\n삭제 후 시도해주세요.'
      );
      return;
    }
    setAttendanceWrappers((prev) => [...prev, ...data]);
  }

  useEffect(() => {
    if (attendanceWrappers.length < 1) return;

    initAttendanceDatas(attendanceWrappers);
  }, [attendanceWrappers]);

  useEffect(() => {
    if (attendanceResults.length < 1) return;
    if (layoutDatas.length < 1) return;
    selectedName === null && setSelectedName(attendanceWrappers[0].name);
  }, [attendanceResults, layoutDatas]);

  useEffect(() => {
    if (!selectedName) return;
    if (attendanceWrappers.length < 1) return;
    setSelectedWrapper(
      attendanceWrappers.find((wrapper) => wrapper.name === selectedName) ||
        null
    );
    setSelectedLayoutData(
      layoutDatas.find((layoutData) => layoutData.name === selectedName) || null
    );
  }, [selectedName]);

  function isRemovable() {
    return !(
      selectedName === null ||
      selectedWrapper === null ||
      selectedLayoutData === null ||
      attendanceResults.length < 1 ||
      attendanceWrappers.length < 1
    );
  }

  function removeData() {
    //선택된 자료가 있어야 동작
    if (!isRemovable()) return;

    const newAttendanceWrappers = attendanceWrappers.filter(
      (attendance) => attendance.name !== selectedName
    );

    const newAttendanceResult = attendanceResults.filter(
      (result) => result.name !== selectedName
    );

    const newLayoutDatas = layoutDatas.filter(
      (layout) => layout.name !== selectedName
    );

    setAttendanceWrappers(newAttendanceWrappers);
    setAttendanceResults(newAttendanceResult);
    setLayoutDatas(newLayoutDatas);

    if (newAttendanceWrappers.length > 1 && newLayoutDatas.length > 1) {
      const nameInWrapper = newAttendanceWrappers[0].name;
      const nameInLayout = newLayoutDatas[0].name;
      const nameInResult = newAttendanceResult[0].name;

      if (nameInWrapper === nameInLayout && nameInLayout === nameInResult) {
        setSelectedName(nameInWrapper);
      } else {
        //초기화
        toast.error('근태기록과 결과가 일치하지 않습니다. 다시 시도해주세요');
        setAttendanceResults([]);
        setLayoutDatas([]);
        setSelectedName(null);
      }
    } else {
      setSelectedName(null);
    }
  }

  function initAttendanceDatas(datas: AttendanceWrapper[]) {
    if (datas.length < 1) return;

    let attendanceResultWrapper: AttendanceResultWrapper[] = [];
    let attendanceLayoutWrapper: LayoutDataType[] = [];

    datas.map((wrapper) => {
      const {
        groupTopIndex, //주휴수당(5일 연속) 시작인덱스
        groupMiddleIndex, //주휴수당(5일 연속) 포함인덱스
        groupBottomIndex, //주휴수당(5일 연속) 끝인덱스
        absentGroupTopIndex, //결근이 포함된 주휴수당(5일 연속) 시작인덱스
        absentGroupMiddleIndex, //결근이 포함된 주휴수당(5일 연속) 포함인덱스
        absentGroupBottomIndex, //결근이 포함된 주휴수당(5일 연속) 끝인덱스
        absentIndex, //결근에 해당하는 인덱스
        groupCount, //주휴수당 포함일 개수
        totalCount, //실제 출근일
        paidToUnpaidResult,
        paidSundayIndexList,
        absentSundayIndexList,
      } = totalDateFn(wrapper, isPartTime);

      //단축근무관련 값
      const {
        shortenedIndecies, //단축근무에 해당하는 index
        unvalidShortenedIndecies, //단축근무에 해당(하는 것으로 예상)하나, 값을 추출할 수 없는 index
        shortenedWorkHour, //단축근무 시간
      } = shortenedWorkHourFn(wrapper);

      //연장근무관련 값
      const {
        overworkTime, //연장근무 시간
        overworkIndicies, //연장근무에 해당하는 index
        unvalidOverworkIndecies, //연장근무에 해당(하는 것으로 예상)하나, 값을 추출할 수 없는 index
      } = overworkHourFn(wrapper);

      //연가 관련값
      const {
        annualLeaveDayIndecies, //연차를 일 단위로 사용한 index
        annualLeaveHourIndecies, //연차를 시간 단위로 사용한 index
        unvalidAnnualLeaveHourIndecies, //연차를 시간 단위로 사용(한 것으로 예상)했으나, 값을 추출할 수 없는 index
        annualResult, //n일 m시간, n일, m시간 등 최종 결과값 string
      } = annualLeaveHourFn(wrapper, isPartTime);

      const {
        sickLeaveDayIndecies,
        sickLeaveHourIndecies,
        unvalidSickLeaveHourIndecies,
        sickLeaveResult,
      } = sickLeaveHourFn(wrapper);

      //결근 관련값
      const { absentDayIndicies, absentDayCount, absentResult } = absentDaysFn(
        wrapper,
        isPartTime
      );

      // 근태변환 결과값 세팅
      const attendanceResult: AttendanceResultType = {
        totalWorkDays: totalCount.toString(),
        shortenedWorkHours: shortenedWorkHour.toString(),
        annualLeaveHours: annualResult,
        overtimeHours: overworkTime.toString(),
        paidToUnpaidHours: paidToUnpaidResult,
        absentDays: absentResult,
        sickLeaveDays: sickLeaveResult,
      };

      attendanceResultWrapper.push({
        name: wrapper.name,
        year: wrapper.year,
        month: wrapper.month,
        data: attendanceResult,
      });

      //근태변환 레이아웃값(테이블에서 사용) 세팅
      const layout: LayoutDataType = {
        name: wrapper.name,
        groupTopIndex,
        groupMiddleIndex,
        groupBottomIndex,

        shortenedIndex: shortenedIndecies,
        unvalidShortenedIndex: unvalidShortenedIndecies,
        overworkIndex: overworkIndicies,
        unvalidOverworkIndex: unvalidOverworkIndecies,
        annualLeaveDayIndecies: annualLeaveDayIndecies,
        annualLeaveHourIndecies: annualLeaveHourIndecies,
        unvalidAnnualLeaveHourIndecies: unvalidAnnualLeaveHourIndecies,
        absentDayIndicies: absentIndex,
        absentGroupTopIndex: absentGroupTopIndex,
        absentGroupMiddleIndex: absentGroupMiddleIndex,
        absentGroupBottomIndex: absentGroupBottomIndex,
        sickLeaveDayIndecies: sickLeaveDayIndecies,
        sickLeaveHourIndecies: sickLeaveHourIndecies,
        unvalidSickLeaveHourIndecies: unvalidSickLeaveHourIndecies,
        paidSundayIndecies: paidSundayIndexList,
        unpaidSundayIndecies: absentSundayIndexList,
      };
      attendanceLayoutWrapper.push(layout);
    });

    setAttendanceResults((prev) => [...prev, ...attendanceResultWrapper]);
    setLayoutDatas((prev) => [...prev, ...attendanceLayoutWrapper]);
  }

  function getChangeReasonType(index: number) {
    //단축근무 index
    const isShortened = selectedLayoutData?.shortenedIndex?.includes(index);
    //연장근무 index
    const isOverwork = selectedLayoutData?.overworkIndex?.includes(index);
    //병가 index
    const isSickLeave =
      selectedLayoutData?.sickLeaveHourIndecies?.includes(index);
    //연가 index
    const isAnnualHour =
      selectedLayoutData?.annualLeaveHourIndecies?.includes(index);
    //유효하지 않은 수정사요
    const isUnvalid =
      selectedLayoutData?.unvalidOverworkIndex?.includes(index) ||
      selectedLayoutData?.unvalidShortenedIndex?.includes(index) ||
      selectedLayoutData?.unvalidShortenedIndex?.includes(index) ||
      selectedLayoutData?.unvalidSickLeaveHourIndecies?.includes(index);
    //1개 이상의 index에 해당할 경우
    const combineIndex = [isShortened, isOverwork, isAnnualHour, isSickLeave];
    const isMixed = combineIndex.filter((item) => item === true).length > 1;

    if (isUnvalid) {
      return 'unvalid';
    } else if (isMixed) {
      return 'mixed';
    } else {
      if (isShortened) return 'shortened';
      if (isOverwork) return 'overwork';
      if (isAnnualHour) return 'annual';
      if (isSickLeave) return 'sick';
    }
  }

  function attendanceResultValueFn<K extends keyof AttendanceResultType>(
    key: K
  ) {
    return (
      (selectedName &&
        attendanceResults.find((attendance) => attendance.name === selectedName)
          ?.data[key]) ||
      undefined
    );
  }

  function attendanceResultSetValueFn<K extends keyof AttendanceResultType>(
    key: K
  ) {
    const setValue = (changedValue: AttendanceResultType[K]) => {
      setAttendanceResults((prev) =>
        prev.map((attendance) =>
          attendance.name === selectedName
            ? {
                ...attendance,
                data: {
                  ...attendance.data,
                  [key]: changedValue, // key를 동적으로 처리
                },
              }
            : attendance
        )
      );
    };
    return { setValue };
  }

  return (
    <Container>
      {!year && !month && <h3>연, 월을 선택해주세요</h3>}
      <StyledDateInput
        id='month-input'
        type='month'
        onChange={handleDateChange}
      />
      <div style={{ height: '15px' }} />
      <FlexRowContainer $gap={30}>
        <FlexRowContainer $gap={8} onClick={() => setIsPartTime(true)}>
          <RadioButton $isChecked={isPartTime} />
          생산직원
        </FlexRowContainer>
        <FlexRowContainer $gap={8} onClick={() => setIsPartTime(false)}>
          <RadioButton $isChecked={!isPartTime} />
          관리직원
        </FlexRowContainer>
      </FlexRowContainer>
      {attendanceResults.length > 0 && (
        <ResultArea>
          <ResultItem>
            <ResultLabel>근무일수(일)</ResultLabel>
            <ResultInput
              value={attendanceResultValueFn('totalWorkDays')}
              onChange={(e) => {
                attendanceResultSetValueFn('totalWorkDays').setValue(
                  e.target.value
                );
              }}
            />
          </ResultItem>
          <ResultItem>
            <ResultLabel>근무단축(시간)</ResultLabel>
            <ResultInput
              value={attendanceResultValueFn('shortenedWorkHours')}
              onChange={(e) => {
                attendanceResultSetValueFn('shortenedWorkHours').setValue(
                  e.target.value
                );
              }}
            />
          </ResultItem>
          <ResultItem>
            <ResultLabel>연가(시간)</ResultLabel>
            <ResultInput
              value={attendanceResultValueFn('annualLeaveHours')}
              onChange={(e) => {
                attendanceResultSetValueFn('annualLeaveHours').setValue(
                  e.target.value
                );
              }}
            />
          </ResultItem>
          <ResultItem>
            <ResultLabel>연장근로(시간)</ResultLabel>
            <ResultInput
              value={attendanceResultValueFn('overtimeHours')}
              onChange={(e) => {
                attendanceResultSetValueFn('overtimeHours').setValue(
                  e.target.value
                );
              }}
            />
          </ResultItem>
          <ResultItem>
            <ResultLabel>유급휴일무급처리(시간)</ResultLabel>
            <ResultInput
              value={attendanceResultValueFn('paidToUnpaidHours')}
              onChange={(e) => {
                attendanceResultSetValueFn('paidToUnpaidHours').setValue(
                  e.target.value
                );
              }}
            />
          </ResultItem>
          <ResultItem>
            <ResultLabel>결근(일)</ResultLabel>
            <ResultInput
              value={attendanceResultValueFn('absentDays')}
              onChange={(e) => {
                attendanceResultSetValueFn('absentDays').setValue(
                  e.target.value
                );
              }}
            />
          </ResultItem>
          <ResultItem>
            <ResultLabel>병가(일)</ResultLabel>
            <ResultInput
              value={attendanceResultValueFn('sickLeaveDays')}
              onChange={(e) => {
                attendanceResultSetValueFn('sickLeaveDays').setValue(
                  e.target.value
                );
              }}
            />
          </ResultItem>
        </ResultArea>
      )}

      {openUploadModal && year && month && (
        <AttendanceUploadModal
          onClose={() => setOpenUploadModal(false)}
          onSelect={(data: AttendanceWrapper[]) => onSelectExcelData(data)}
          year={year}
          month={month}
        />
      )}
      {attendanceWrappers.length > 0 && selectedName !== null && (
        <>
          <NameSection>
            {attendanceWrappers.map((wrapper, index) => (
              <NameSelect
                selected={selectedName === wrapper.name}
                isFirstItem={index === 0}
                isLastItem={index + 1 === attendanceWrappers.length}
                isFixed={fixedNames.includes(wrapper.name)}
                onClick={() => setSelectedName(wrapper.name)}
              >
                {wrapper.name}
              </NameSelect>
            ))}
          </NameSection>
          {selectedWrapper && selectedLayoutData && (
            <>
              <TableSection>
                <tr style={{ borderCollapse: 'collapse' }}>
                  <th className='date'>근무일자</th>
                  <th className='name'>이름</th>
                  <th className='schedule'>근무스케줄</th>
                  <th className='clock-in-time'>출근시간</th>
                  <th className='clock-out-time'>퇴근시간</th>
                  <th className='clock-in-status'>출근판정</th>
                  <th className='clock-out-status'>퇴근판정</th>
                  <th className='holiday-work-time'>휴일근무시간</th>
                  <th className='workday-work-time'>정상근무시간</th>
                  <th className='change-reason'>수정 사유</th>
                  <th className='over-work-time'>연장근무시간</th>
                </tr>
                {selectedWrapper.data.map((data, index) => (
                  <StyledTr
                    $isAbsentRow={selectedLayoutData.absentDayIndicies?.includes(
                      index
                    )}
                  >
                    <td className='date'>{data.date}</td>
                    <td className='name'>{data.name}</td>
                    <ScheduleTd
                      className={`schedule`}
                      $groupType={
                        selectedLayoutData.groupTopIndex.includes(index)
                          ? 'top-item'
                          : selectedLayoutData.groupMiddleIndex.includes(index)
                          ? 'middle-item'
                          : selectedLayoutData.groupBottomIndex.includes(index)
                          ? 'bottom-item'
                          : selectedLayoutData.absentGroupTopIndex?.includes(
                              index
                            )
                          ? 'absent-top-item'
                          : selectedLayoutData.absentGroupMiddleIndex?.includes(
                              index
                            )
                          ? 'absent-middle-item'
                          : selectedLayoutData.absentGroupBottomIndex?.includes(
                              index
                            )
                          ? 'absent-bottom-item'
                          : undefined
                      }
                      $isAnnualLeaveDay={selectedLayoutData.annualLeaveDayIndecies?.includes(
                        index
                      )}
                      $isAbsentDay={selectedLayoutData.absentDayIndicies?.includes(
                        index
                      )}
                      $isSickLeaveDay={selectedLayoutData.sickLeaveDayIndecies?.includes(
                        index
                      )}
                      $isPaidSunday={selectedLayoutData.paidSundayIndecies?.includes(
                        index
                      )}
                      $isUnpaidSunday={selectedLayoutData.unpaidSundayIndecies?.includes(
                        index
                      )}
                    >
                      {data.schedule}
                      {selectedLayoutData.paidSundayIndecies?.includes(index) &&
                        '-유급처리'}
                      {selectedLayoutData.unpaidSundayIndecies?.includes(
                        index
                      ) && '-무급처리'}
                    </ScheduleTd>
                    <td className='clock-in-time'>{data.clockInTime}</td>
                    <td className='clock-out-time'>{data.clockOutTime}</td>
                    <td className='clock-in-status'>{data.clockInStatus}</td>
                    <td className='clock-out-status'>{data.clockOutStatus}</td>
                    <td className='holiday-work-time'>
                      {data.holidayWorkTime}
                    </td>
                    <td className='workday-work-time'>
                      {data.workdayWorkTime}
                    </td>
                    <ChangeReasonTd
                      className='change-reason'
                      $reasonType={
                        getChangeReasonType(index) === 'overwork'
                          ? data.changeReason !== null &&
                            (data.changeReason.includes('연장') ||
                              data.changeReason.includes('인정'))
                            ? 'overwork-double'
                            : 'overwork-single'
                          : getChangeReasonType(index)
                      }
                    >
                      {data.changeReason}
                    </ChangeReasonTd>
                    <OverWorkTimeTd
                      className='over-work-time'
                      $highlightType={
                        selectedLayoutData.overworkIndex?.includes(index)
                          ? data.changeReason !== null &&
                            (data.changeReason.includes('연장') ||
                              data.changeReason.includes('인정'))
                            ? 'double'
                            : 'single'
                          : undefined
                      }
                    >
                      {data.overWorkTime}
                    </OverWorkTimeTd>
                  </StyledTr>
                ))}
              </TableSection>
            </>
          )}
        </>
      )}
    </Container>
  );
};

const Container = styled.div`
  * {
    box-sizing: border-box;
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  gap: 30px;
`;

const StyledDateInput = styled.input`
  width: 150px;
  height: 25px;
  padding: 7px;
  font-size: 17px;
`;

const NameSection = styled.div`
  display: flex;
  margin-top: 15px;
  flex-direction: row;
  align-items: center;
  width: fit-content;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const NameSelect = styled.div<{
  selected: boolean;
  isFirstItem: boolean;
  isLastItem: boolean;
  isFixed: boolean;
}>`
  cursor: pointer;

  padding: 8px 12px;
  font-weight: 600;
  border-bottom: ${({ isFixed, theme }) =>
    isFixed ? '1px solid transparent' : `1px solid ${theme.colors.red}`};
  align-content: center;
  text-align: center;
  background-color: ${({ selected, theme }) =>
    selected ? theme.colors.white : theme.colors.white_odd};

  border-top-left-radius: ${({ isFirstItem }) => (isFirstItem ? '15px' : 0)};
  border-top-right-radius: ${({ isLastItem }) => (isLastItem ? '15px' : 0)};

  &:hover {
    background-color: rgb(255 255 255 / 4%);
  }
`;

const TableSection = styled.div`
  box-sizing: border-box;

  /* table-layout: fixed; */

  th,
  td {
    padding: 5px;
    align-content: center;
    text-align: center;
  }

  th {
    border: ${({ theme }) => `1px solid ${theme.colors.charcoal}`};
    background-color: white;
  }

  td {
    background-color: white;
  }

  .date {
    width: 150px;
  }
  .name {
    width: 110px;
  }
  .schedule {
    min-width: 180px;
  }
  .clock-in-time {
    width: 210px;
  }
  .clock-out-time {
    width: 210px;
  }
  .clock-in-status {
    width: 130px;
  }
  .clock-out-status {
    width: 130px;
  }
  .holiday-work-time {
    width: 85px;
  }
  .workday-work-time {
    width: 85px;
  }
  .over-work-time {
    width: 85px;
  }
  .change-reason {
    /* width: 160px; */
    min-width: 240px;
    max-width: 240px;
  }
`;

const ResultArea = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  margin-top: 15px;
  gap: 15px;
`;

const FlexRowContainer = styled.div<{ $gap: number }>`
  display: flex;
  flex-direction: row;
  gap: ${({ $gap }) => `${$gap}px`};
  align-items: center;
`;

const RadioButton = styled.div<{ $isChecked: boolean }>`
  cursor: pointer;
  text-align: center;
  align-content: center;
  width: 15px;
  height: 15px;
  border-radius: 100%;
  border: ${({ theme, $isChecked }) =>
    $isChecked ? `1px solid #4ba7ff` : `1px solid ${theme.colors.charcoal}`};
  background-color: ${({ $isChecked }) =>
    $isChecked ? '#4ba7ff' : 'transparent'};
  position: relative; /* 상대 위치 설정 */

  &::before {
    content: '';
    background-color: white;
    border-radius: 100%;
    width: 50%;
    height: 50%;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%); /* 정중앙 정렬 */
    display: ${({ $isChecked }) =>
      $isChecked ? 'block' : 'none'}; /* 체크된 경우만 표시 */
  }
`;

const ResultItem = styled.div<{ width?: number }>`
  width: ${({ width }) => (width ? `${width}px` : 'auto')};
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const ResultLabel = styled.h4`
  margin: 5px 0px 0px 0px;
`;

const ResultInput = styled.input`
  margin: 7px 0px;
  font-size: 18px;
  width: 80%;
  text-align: center;
  padding: 0px 5px;
  height: 22px;
`;

const StyledTr = styled.tr<{ $isAbsentRow?: boolean }>`
  td {
    background-color: ${({ $isAbsentRow }) =>
      $isAbsentRow ? '#ffd1d1' : 'white'};
  }
`;

const ScheduleTd = styled.td<{
  $groupType?: string;
  $isAnnualLeaveDay?: boolean;
  $isAbsentDay?: boolean;
  $isSickLeaveDay?: boolean;
  $isPaidSunday?: boolean;
  $isUnpaidSunday?: boolean;
}>`
  //기본보더 - 공간 차지, 투명색
  border-width: 1px;
  border-style: solid;
  border-color: transparent;

  //그룹 최상단아이템은 보더 탑 적용
  border-top-color: ${({ $groupType }) =>
    $groupType === 'top-item'
      ? 'green'
      : $groupType === 'absent-top-item'
      ? 'red'
      : undefined};
  //그룹 최하단아이템은 보더 바텀 적용
  border-bottom-color: ${({ $groupType }) =>
    $groupType === 'bottom-item'
      ? 'green'
      : $groupType === 'absent-bottom-item'
      ? 'red'
      : undefined};

  //그룹 내부아이템(탑, 바텀 포함)은 좌/우 보더 적용
  border-left-color: ${({ $groupType }) =>
    $groupType && !$groupType?.startsWith('absent')
      ? 'green'
      : $groupType
      ? 'red'
      : undefined};
  border-right-color: ${({ $groupType }) =>
    $groupType && !$groupType?.startsWith('absent')
      ? 'green'
      : $groupType
      ? 'red'
      : undefined};

  background-color: ${({ $isAnnualLeaveDay }) =>
    $isAnnualLeaveDay && '#ffc9aa !important'};
  background-color: ${({ $isAbsentDay }) =>
    $isAbsentDay && '#ffd1d1 !important'};
  background-color: ${({ $isSickLeaveDay }) =>
    $isSickLeaveDay && '#d5e8ff !important'};

  ${({ $isPaidSunday }) =>
    $isPaidSunday &&
    css`
      font-style: italic;
      color: ${({ theme }) => theme.colors.green};
      font-weight: 600;
    `}

  ${({ $isUnpaidSunday }) =>
    $isUnpaidSunday &&
    css`
      font-style: italic;
      color: ${({ theme }) => theme.colors.red};
      font-weight: 600;
    `}
`;

const ChangeReasonTd = styled.td<{ $reasonType?: string }>`
  //기본보더 - 공간 차지, 투명색
  border-width: 1px;
  border-style: solid;
  border-color: ${({ $reasonType }) =>
    $reasonType === 'shortened'
      ? 'black'
      : $reasonType === 'overwork-double'
      ? '#ec4bff'
      : $reasonType === 'mixed'
      ? '#81C784'
      : $reasonType === 'annual'
      ? '#ffc9aa'
      : $reasonType === 'sick'
      ? '#64B5F6'
      : $reasonType === 'unvalid'
      ? 'red'
      : 'transparent'};
  background-color: ${({ $reasonType }) =>
    $reasonType === 'unvalid' && '#ffd9d9 !important'};
  border-right-width: ${({ $reasonType }) =>
    $reasonType === 'overwork-double' && '0'};
`;

const OverWorkTimeTd = styled.td<{ $highlightType?: string }>`
  //기본보더 - 공간 차지, 투명색
  border-width: 1px;
  border-style: solid;
  border-color: ${({ $highlightType }) =>
    $highlightType ? '#ec4bff' : 'transparent'};
  border-left-width: ${({ $highlightType }) =>
    $highlightType === 'double' ? 0 : '1px'};
`;
